import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AudioLines, Check, Loader2, Pencil, Play, RotateCcw, Search, Star, X } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Cover, DragHandle, DropLine, IconButton, useDragOrder } from '@/components/music';

/**
 * Radio tab on the Music page: the server's favorite stations (web-api
 * routes/radios.ts, default list until someone changes it) and a search over
 * radio-browser.info. A station replaces the queue (bot functions/lofi.js).
 * Everything here needs the Music edit right; view-only people see the
 * favorites disabled (Music.tsx fieldset).
 */

export interface Station {
  id: string;
  name: string;
  url: string;
  favicon: string | null;
  tags: string[];
  country: string | null;
  codec: string | null;
  bitrate: number | null;
}

interface Favorites {
  stations: Station[];
  /** false: the default list (nothing saved for this server yet). */
  custom: boolean;
}

const MAX_FAVORITES = 30;
const radiosKey = (guildId: string) => ['radios', guildId];

const meta = (s: Station) =>
  [s.tags.slice(0, 2).join(', '), s.country, s.bitrate && `${s.bitrate} kbit/s`].filter(Boolean).join(' · ');

function useDebounced<T>(value: T, ms: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function RadioTab({
  guildId,
  canEdit,
  canPlay,
  playHint,
  playingId,
  startingId,
  onPlay,
}: {
  guildId: string;
  canEdit: boolean;
  canPlay: boolean;
  /** Why ▶ is off (no edit right / no voice channel). */
  playHint: string | null;
  /** Station that plays right now (state.queue[0].stationId). */
  playingId: string | null;
  /** Sent to the bot, not playing yet. */
  startingId: string | null;
  onPlay: (stationId: string) => void;
}) {
  const qc = useQueryClient();
  const url = `/api/guilds/${guildId}/music/radios`;
  const favs = useQuery({ queryKey: radiosKey(guildId), queryFn: () => api<Favorites>(url) });
  const setFavs = (data: unknown) => qc.setQueryData(radiosKey(guildId), data as Favorites);
  const add = useMutation({
    mutationFn: (stationId: string) => api<Favorites>(url, { method: 'POST', body: JSON.stringify({ stationId }) }),
    onSuccess: setFavs,
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api<Favorites>(url, { method: 'PUT', body: JSON.stringify({ ids }) }),
    onSuccess: setFavs,
  });
  const reset = useMutation({
    mutationFn: () => api<Favorites>(url, { method: 'DELETE' }),
    onSuccess: (data) => {
      setFavs(data);
      setConfirmReset(false);
    },
  });
  const [editing, setEditing] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const stations = favs.data?.stations ?? [];
  const favIds = new Set(stations.map((s) => s.id));
  const error = [add, reorder, reset].find((m) => m.error)?.error as Error | undefined;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3 space-y-0">
          <div className="min-w-0">
            <CardTitle className="text-base">Favoriten</CardTitle>
            <CardDescription>
              Ein Klick startet den Sender — er ersetzt die Warteschlange, ⏹ beendet ihn. In Discord: <code>/24-7</code>{' '}
              für Lo-fi.
            </CardDescription>
          </div>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              {editing && favs.data?.custom && (
                confirmReset ? (
                  <span className="flex items-center gap-2 text-sm">
                    Grundliste wiederherstellen?
                    <Button size="sm" variant="destructive" disabled={reset.isPending} onClick={() => reset.mutate()}>
                      Ja
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmReset(false)}>
                      Nein
                    </Button>
                  </span>
                ) : (
                  <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => setConfirmReset(true)}>
                    <RotateCcw className="h-4 w-4" /> Grundliste
                  </Button>
                )
              )}
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditing(!editing)}>
                {editing ? <Check className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                {editing ? 'Fertig' : 'Bearbeiten'}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {playHint && !editing && <p className="text-xs text-muted-foreground">{playHint}</p>}
          {favs.isLoading && <p className="text-sm text-muted-foreground">Lade Sender…</p>}
          {favs.error && <p className="text-sm text-destructive">{(favs.error as Error).message}</p>}
          {favs.data && stations.length === 0 && (
            <p className="text-sm text-muted-foreground">Keine Favoriten — unten einen Sender suchen und mit ☆ merken.</p>
          )}
          {editing ? (
            <FavoriteEditList stations={stations} busy={reorder.isPending} onOrder={(ids) => reorder.mutate(ids)} />
          ) : (
            <div className="grid grid-cols-[minmax(0,1fr)] gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {stations.map((s) => (
                <StationTile
                  key={s.id}
                  station={s}
                  playing={playingId === s.id}
                  starting={startingId === s.id}
                  disabled={!canPlay || !!startingId}
                  onPlay={() => onPlay(s.id)}
                />
              ))}
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error.message}</p>}
        </CardContent>
      </Card>

      {canEdit && (
        <StationSearch
          guildId={guildId}
          canPlay={canPlay && !startingId}
          playingId={playingId}
          startingId={startingId}
          favIds={favIds}
          full={stations.length >= MAX_FAVORITES}
          adding={add.isPending ? add.variables : null}
          onPlay={onPlay}
          onAdd={(id) => add.mutate(id)}
        />
      )}
    </div>
  );
}

function StationTile({
  station,
  playing,
  starting,
  disabled,
  onPlay,
}: {
  station: Station;
  playing: boolean;
  starting: boolean;
  disabled: boolean;
  onPlay: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPlay}
      title={`${station.name} abspielen`}
      className={cn(
        'group flex items-center gap-3 rounded-lg border bg-card p-2 text-left transition-colors hover:border-primary/60 disabled:cursor-not-allowed disabled:hover:border-border',
        playing && 'border-primary bg-primary/10 disabled:hover:border-primary'
      )}
    >
      <Cover src={station.favicon} radio className="h-12 w-12" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{station.name}</div>
        <div className={cn('truncate text-xs', playing ? 'text-primary' : 'text-muted-foreground')}>
          {playing ? 'Läuft gerade' : starting ? 'Startet…' : meta(station) || 'Radio'}
        </div>
      </div>
      <span className="shrink-0 pr-1 text-primary">
        {starting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : playing ? (
          <AudioLines className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4 fill-current opacity-0 transition-opacity group-hover:opacity-100 group-disabled:opacity-0" />
        )}
      </span>
    </button>
  );
}

/** Edit mode: drag to sort, × to remove — saved right away. */
function FavoriteEditList({ stations, busy, onOrder }: { stations: Station[]; busy: boolean; onOrder: (ids: string[]) => void }) {
  // Shown until the saved list comes back.
  const [order, setOrder] = useState<string[] | null>(null);
  useEffect(() => setOrder(null), [stations]);
  const byId = new Map(stations.map((s) => [s.id, s]));
  const shown = (order ?? stations.map((s) => s.id)).map((id) => byId.get(id)).filter((s): s is Station => !!s);

  const save = (ids: string[]) => {
    setOrder(ids);
    onOrder(ids);
  };
  const { listRef, draggingIndex, handle, lineAt } = useDragOrder(shown.length, (from, to) => {
    const ids = shown.map((s) => s.id);
    ids.splice(to, 0, ...ids.splice(from, 1));
    save(ids);
  });

  return (
    <ul ref={listRef} className="divide-y rounded-lg border bg-card">
      {shown.map((s, i) => {
        const line = lineAt(i);
        return (
          <li key={s.id} className={cn('relative flex items-center gap-2 p-2 text-sm sm:gap-3', draggingIndex === i && 'bg-accent/60 opacity-60')}>
            {line && <DropLine position={line} />}
            <DragHandle {...handle(i)} disabled={busy} />
            <Cover src={s.favicon} radio className="h-9 w-9" />
            <div className="min-w-0 flex-1">
              <div className="truncate">{s.name}</div>
              <div className="truncate text-xs text-muted-foreground">{meta(s)}</div>
            </div>
            <IconButton
              label="Aus den Favoriten entfernen"
              className="h-8 w-8"
              disabled={busy}
              onClick={() => save(shown.filter((x) => x.id !== s.id).map((x) => x.id))}
            >
              <X className="h-4 w-4" />
            </IconButton>
          </li>
        );
      })}
    </ul>
  );
}

function StationSearch({
  guildId,
  canPlay,
  playingId,
  startingId,
  favIds,
  full,
  adding,
  onPlay,
  onAdd,
}: {
  guildId: string;
  canPlay: boolean;
  playingId: string | null;
  startingId: string | null;
  favIds: Set<string>;
  full: boolean;
  adding: string | null;
  onPlay: (stationId: string) => void;
  onAdd: (stationId: string) => void;
}) {
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 400);
  const search = useQuery({
    queryKey: ['radio-search', guildId, q],
    queryFn: () => api<{ results: Station[] }>(`/api/guilds/${guildId}/music/radio-search?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
    staleTime: 5 * 60_000,
  });
  const results = q.length >= 2 ? (search.data?.results ?? []) : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Sender suchen</CardTitle>
        <CardDescription>
          Über 50.000 Sender von radio-browser.info — nach Name oder Genre, z. B. „Techno“, „Deutschrap“, „Antenne“.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={100} placeholder="Sender oder Genre" className="pl-9 pr-9" />
          {search.isFetching && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
        </div>
        {search.error && <p className="text-sm text-destructive">{(search.error as Error).message}</p>}
        {q.length >= 2 && search.data && results.length === 0 && <p className="text-sm text-muted-foreground">Nichts gefunden.</p>}
        {results.length > 0 && (
          <ul className="divide-y rounded-lg border bg-card">
            {results.map((s) => {
              const fav = favIds.has(s.id);
              return (
                <li key={s.id} className={cn('flex items-center gap-2 p-2 text-sm sm:gap-3', playingId === s.id && 'bg-primary/10')}>
                  <Cover src={s.favicon} radio className="h-10 w-10" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate" title={s.name}>
                      {s.name}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {playingId === s.id ? 'Läuft gerade' : meta(s) || 'Radio'}
                    </div>
                  </div>
                  <IconButton label="Abspielen" className="h-8 w-8" disabled={!canPlay} onClick={() => onPlay(s.id)}>
                    {startingId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  </IconButton>
                  <IconButton
                    label={fav ? 'Schon in den Favoriten' : full ? `Höchstens ${MAX_FAVORITES} Favoriten` : 'Zu den Favoriten'}
                    className="h-8 w-8"
                    disabled={fav || full || !!adding}
                    onClick={() => onAdd(s.id)}
                  >
                    {adding === s.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Star className={cn('h-4 w-4', fav && 'fill-current text-primary')} />
                    )}
                  </IconButton>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
